/**
 * RiveController
 * 
 * Controls the Rive animation for the Squiggle mascot.
 * Provides a clean API for mood changes, talking state, and gesture triggers.
 */

import { IRiveController } from '../types';
import { MoodType, GestureType, Position } from '../types';

// Local type for state machine input
interface StateMachineInput {
  name: string;
  value?: number | boolean;
  fire?: () => void;
}

// Dynamic import of Rive to handle missing package gracefully
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let RiveRuntime: any = null;
let RiveLayout: any = null;
let RiveFit: any = null;
let RiveAlignment: any = null;

try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const riveModule = require('@rive-app/canvas');
  RiveRuntime = riveModule.Rive;
  RiveLayout = riveModule.Layout;
  RiveFit = riveModule.Fit;
  RiveAlignment = riveModule.Alignment;
} catch {
  // Rive not available, will use fallback mode
  console.warn('[RiveController] @rive-app/canvas not available');
}

// Mood to state machine number mapping
const MOOD_MAP: Record<MoodType, number> = {
  idle: 0,
  happy: 1,
  curious: 2,
  confused: 3,
  excited: 4,
};

// State machine input names (must match Rive file)
const INPUT_NAMES = {
  mood: 'mood',
  talking: 'talking',
  // Triggers
  arrive: 'trigger_arrive',
  celebrate: 'trigger_celebrate',
  wave: 'trigger_wave',
  point: 'trigger_point',
  shrug: 'trigger_shrug',
} as const;

// Default Rive file path
const DEFAULT_RIV_PATH = '/assets/squiggle.riv';

export class RiveController implements IRiveController {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rive: any = null;
  private canvas: HTMLCanvasElement | null = null;
  private inputs: Map<string, StateMachineInput> = new Map();
  private loaded: boolean = false;
  private rivePath: string;
  
  // Fallback mode when Rive file doesn't exist
  private fallbackMode: boolean = false;
  private currentMood: MoodType = 'idle';
  private isTalking: boolean = false;

  constructor(rivePath: string = DEFAULT_RIV_PATH) {
    this.rivePath = rivePath;
  }

  /**
   * Load the Rive file and initialize the state machine
   */
  async load(canvas: HTMLCanvasElement): Promise<void> {
    this.canvas = canvas;
    
    // Check if Rive runtime is available
    if (!RiveRuntime) {
      console.warn('[RiveController] Rive runtime not available, using fallback mode');
      this.fallbackMode = true;
      this.loaded = true;
      return;
    }
    
    try {
      // Check if Rive file exists
      const response = await fetch(this.rivePath, { method: 'HEAD' });
      if (!response.ok) {
        console.warn('[RiveController] Rive file not found, using fallback mode');
        this.fallbackMode = true;
        this.loaded = true;
        return;
      }

      const layoutConfig = RiveLayout && RiveFit && RiveAlignment 
        ? new RiveLayout({ fit: RiveFit.Contain, alignment: RiveAlignment.Center })
        : undefined;

      // Wait for Rive to load or fail
      await new Promise<void>((resolve) => {
        this.rive = new RiveRuntime({
          src: this.rivePath,
          canvas: canvas,
          autoplay: true,
          stateMachines: 'SquiggleStateMachine', // Must match Rive file
          layout: layoutConfig,
          onLoad: () => {
            this.initializeInputs();
            this.loaded = true;
            resolve();
          },
          onLoadError: (err: unknown) => {
            console.warn('[RiveController] Failed to load Rive file, using fallback:', err);
            this.fallbackMode = true;
            this.loaded = true;
            resolve();
          },
        });
      });
    } catch (error) {
      console.warn('[RiveController] Error loading Rive, using fallback:', error);
      this.fallbackMode = true;
      this.loaded = true;
    }
  }

  /**
   * Initialize state machine inputs
   */
  private initializeInputs(): void {
    if (!this.rive) return;

    const stateMachineInputs = this.rive.stateMachineInputs('SquiggleStateMachine');
    if (!stateMachineInputs) {
      console.warn('[RiveController] No state machine inputs found');
      return;
    }

    // Store all inputs by name
    for (const input of stateMachineInputs) {
      this.inputs.set(input.name, input);
    }
  }

  /**
   * Set the mood of the mascot
   */
  setMood(mood: MoodType): void {
    this.currentMood = mood;
    
    if (this.fallbackMode) return;
    
    const moodInput = this.inputs.get(INPUT_NAMES.mood);
    if (moodInput && 'value' in moodInput) {
      moodInput.value = MOOD_MAP[mood];
    }
  }

  /**
   * Set talking state (for mouth animation)
   */
  setTalking(talking: boolean): void {
    this.isTalking = talking;
    
    if (this.fallbackMode) return;
    
    const talkingInput = this.inputs.get(INPUT_NAMES.talking);
    if (talkingInput && 'value' in talkingInput) {
      talkingInput.value = talking;
    }
  }

  /**
   * Trigger a gesture animation
   */
  trigger(gesture: GestureType): void {
    if (this.fallbackMode) return;
    
    const inputName = INPUT_NAMES[gesture];
    const triggerInput = this.inputs.get(inputName);
    
    if (triggerInput && 'fire' in triggerInput && typeof triggerInput.fire === 'function') {
      triggerInput.fire();
    }
  }

  /**
   * Get the anchor point for trail attachment
   * Returns the center of the mascot canvas
   */
  getAnchorPoint(): Position {
    if (!this.canvas) {
      return { x: 0, y: 0 };
    }
    
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }

  /**
   * Check if the controller is loaded
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Check if in fallback mode (no Rive file)
   */
  isFallbackMode(): boolean {
    return this.fallbackMode;
  }

  /**
   * Get current mood (for fallback rendering)
   */
  getCurrentMood(): MoodType {
    return this.currentMood;
  }

  /**
   * Get talking state (for fallback rendering)
   */
  getTalking(): boolean {
    return this.isTalking;
  }

  /**
   * Resize the Rive canvas
   */
  resize(): void {
    if (this.rive) {
      this.rive.resizeDrawingSurfaceToCanvas();
    }
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    if (this.rive) {
      this.rive.cleanup();
      this.rive = null;
    }
    this.inputs.clear();
    this.canvas = null;
    this.loaded = false;
  }
}

export default RiveController;
